import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import React from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/button/button';
import OptionRow from '@/components/optionRow/optionRow';
import { SOURCE_TYPE_DISPLAY_NAMES, SOURCE_TYPES, SourceTypeEnum } from '@/types/evals';

import styles from './modalSourceCreate.module.scss';
import { GeneralInfoFormData,generalInfoSchema } from './schemas';

type StepGeneralProps = {
  initialData?: GeneralInfoFormData;
  onNext: ( data: GeneralInfoFormData ) => void;
  onCancel: () => void;
  isPending?: boolean;
};

export const StepGeneral: React.FC<StepGeneralProps> = ( {
  initialData,
  onNext,
  onCancel,
  isPending
} ) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<GeneralInfoFormData>( {
    resolver: zodResolver( generalInfoSchema ),
    defaultValues: initialData
  } );

  const sourceType = watch( 'source_type' );

  const onSubmit = ( data: GeneralInfoFormData ) => {
    onNext( data );
  };

  return (
    <form onSubmit={ handleSubmit( onSubmit ) } className={ styles.form_wrapper }>
      <div className={ styles.form_content }>
        <h2>Create Source</h2>
        <div className={ clsx( 'layout__col', 'pad' ) }>
          <label
            className={ clsx( 'auth_flow_label', errors.name && 'required' ) }
            htmlFor="name"
          >
            Name <span className={ styles.required_asterisk }>*</span>
          </label>
          <input
            className={ clsx( 'auth_flow_input', errors.name && styles.input_error ) }
            id="name"
            type="text"
            data-testid="source-name-input"
            disabled={ isPending }
            { ...register( 'name' ) }
          />
          {errors.name && (
            <div className={ styles.error_message }>
              {errors.name.message || 'This field is required'}
            </div>
          )}
        </div>
        <div className={ clsx( 'layout__col', 'pad' ) }>
          <label className={ clsx( 'auth_flow_label' ) } htmlFor="description">
            Description
          </label>
          <textarea
            className={ clsx( 'auth_flow_input' ) }
            id="description"
            rows={ 3 }
            placeholder="Optional description for this source"
            data-testid="source-description-input"
            disabled={ isPending }
            { ...register( 'description' ) }
          />
        </div>
        <div data-testid="source-type-selection">
          <OptionRow
            value={ sourceType }
            onSelect={ ( value: SourceTypeEnum ) => setValue( 'source_type', value ) }
            title={
              (
                <p className={ clsx( errors.source_type && 'required' ) }>
                  Select Source Type <span className={ styles.required_asterisk }>*</span>
                </p>
              )
            }
            options={ [
              {
                display: SOURCE_TYPE_DISPLAY_NAMES[SOURCE_TYPES.SNOWFLAKE],
                value: SOURCE_TYPES.SNOWFLAKE
              },
              {
                display: SOURCE_TYPE_DISPLAY_NAMES[SOURCE_TYPES.AGENT_ACTION_LOGS],
                value: SOURCE_TYPES.AGENT_ACTION_LOGS
              },
              {
                display: SOURCE_TYPE_DISPLAY_NAMES[SOURCE_TYPES.MODEL_INVOCATION_LOGS],
                value: SOURCE_TYPES.MODEL_INVOCATION_LOGS
              },
              {
                display: SOURCE_TYPE_DISPLAY_NAMES[SOURCE_TYPES.COMPLETION_LOGS],
                value: SOURCE_TYPES.COMPLETION_LOGS
              },
              {
                display: SOURCE_TYPE_DISPLAY_NAMES.json,
                value: SOURCE_TYPES.JSON
              }
            ] }
            disabled={ isPending }
          />
          {errors.source_type && (
            <div className={ styles.error_message }>
              {errors.source_type.message || 'Please select a source type'}
            </div>
          )}
        </div>
      </div>
      <div className={ clsx( 'layout__row', 'pad', 'flex_right', styles.form_actions ) }>
        <Button
          variant="secondary"
          size="sm"
          onClick={ onCancel }
          disabled={ isPending }
          data-testid="source-cancel-button"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          type="submit"
          data-testid="source-next-button"
          disabled={ isPending }
        >
          Next
        </Button>
      </div>
    </form>
  );
};
